import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Button,
    Input,
    message,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {
    ReloadOutlined,
    SearchOutlined,
    SyncOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../../../services/api';
import { fetchShopifyStores } from '../../ShopifySettings/actions';

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_PAGINATION = {
    current: 1,
    pageSize: 50,
    total: 0
};

const ShopifyCollectionSettings = () => {
    const dispatch = useDispatch();
    const { shopifyStores = [], loading: storesLoading = false } = useSelector(
        state => state.shopifySettings || { shopifyStores: [], loading: false }
    );

    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [filters, setFilters] = useState({
        search: '',
        shopifyStoreId: 'all',
        status: 'active',
        collectionType: 'all'
    });

    useEffect(() => {
        dispatch(fetchShopifyStores());
    }, [dispatch]);

    const fetchCollections = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const queryParams = {
                page: params.page || pagination.current || 1,
                pageSize: params.pageSize || pagination.pageSize || 50,
                search: params.search !== undefined ? params.search : filters.search,
                shopifyStoreId: params.shopifyStoreId !== undefined ? params.shopifyStoreId : filters.shopifyStoreId,
                status: params.status !== undefined ? params.status : filters.status,
                collectionType: params.collectionType !== undefined ? params.collectionType : filters.collectionType,
                sortBy: params.sortBy || 'title',
                sortOrder: params.sortOrder || 'ASC'
            };

            const response = await api.get('/shopify/categories/collections', { params: queryParams });
            setCollections(response.data.rows || []);
            setPagination({
                current: response.data.pagination?.page || queryParams.page,
                pageSize: response.data.pagination?.pageSize || queryParams.pageSize,
                total: response.data.pagination?.total || 0
            });
        } catch (error) {
            console.error('Error fetching Shopify collections:', error);
            message.error(error.response?.data?.message || 'Không thể tải Shopify collections');
        } finally {
            setLoading(false);
        }
    }, [filters, pagination]);

    useEffect(() => {
        fetchCollections({ page: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (patch) => {
        const nextFilters = { ...filters, ...patch };
        setFilters(nextFilters);
        fetchCollections({ ...nextFilters, page: 1 });
    };

    const handleSearch = () => {
        fetchCollections({ page: 1 });
    };

    const handleClearFilters = () => {
        const nextFilters = {
            search: '',
            shopifyStoreId: 'all',
            status: 'active',
            collectionType: 'all'
        };
        setFilters(nextFilters);
        fetchCollections({ ...nextFilters, page: 1 });
    };

    const handleTableChange = (nextPagination, tableFilters, sorter) => {
        fetchCollections({
            page: nextPagination.current,
            pageSize: nextPagination.pageSize,
            sortBy: sorter.field || sorter.columnKey || 'title',
            sortOrder: sorter.order === 'descend' ? 'DESC' : 'ASC'
        });
    };

    const handleSyncCollections = async () => {
        const selectedStoreIds = filters.shopifyStoreId === 'all'
            ? shopifyStores.map(store => store.id)
            : [filters.shopifyStoreId];

        if (!selectedStoreIds.length) {
            message.warning('Chưa có Shopify store để sync');
            return;
        }

        setSyncing(true);
        try {
            const response = await api.post('/shopify/categories/collections/sync', {
                shopifyStoreIds: selectedStoreIds
            });

            message.success(response.data?.message || 'Đã gửi task sync collections');
        } catch (error) {
            console.error('Error syncing Shopify collections:', error);
            message.error(error.response?.data?.message || 'Không thể gửi task sync collections');
        } finally {
            setSyncing(false);
        }
    };

    const storeOptions = useMemo(() => shopifyStores.map(store => ({
        label: `${store.storeName} (${store.shopifyDomain})`,
        value: store.id
    })), [shopifyStores]);

    const columns = [
        {
            title: 'Collection',
            dataIndex: 'title',
            key: 'title',
            sorter: true,
            width: 260,
            render: (title, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{title}</Text>
                    <Text type="secondary">{record.handle || '-'}</Text>
                </Space>
            )
        },
        {
            title: 'Store',
            dataIndex: ['store', 'storeName'],
            key: 'store',
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.store?.storeName || '-'}</Text>
                    <Text type="secondary">{record.store?.shopifyDomain || ''}</Text>
                </Space>
            )
        },
        {
            title: 'Type',
            dataIndex: 'collectionType',
            key: 'collectionType',
            width: 100,
            sorter: true,
            render: (type) => (
                <Tag color={type === 'smart' ? 'purple' : 'blue'}>
                    {type || 'custom'}
                </Tag>
            )
        },
        {
            title: 'Shopify ID',
            dataIndex: 'shopifyCollectionId',
            key: 'shopifyCollectionId',
            width: 150
        },
        {
            title: 'Products',
            dataIndex: 'productsCount',
            key: 'productsCount',
            width: 110,
            sorter: true,
            render: (count) => <Text>{count > 0 ? count : '-'}</Text>
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 120,
            render: (isActive) => (
                <Badge
                    status={isActive ? 'success' : 'default'}
                    text={isActive ? 'Active' : 'Inactive'}
                />
            )
        },
        {
            title: 'Last Seen',
            dataIndex: 'lastSeenAt',
            key: 'lastSeenAt',
            width: 180,
            sorter: true,
            render: (value) => value ? new Date(value).toLocaleString() : '-'
        },
        {
            title: 'Shopify Updated',
            dataIndex: 'shopifyUpdatedAt',
            key: 'shopifyUpdatedAt',
            width: 180,
            render: (value) => value ? new Date(value).toLocaleString() : '-'
        }
    ];

    return (
        <div>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Search title, handle, ID"
                        value={filters.search}
                        onChange={(event) => setFilters(prev => ({ ...prev, search: event.target.value }))}
                        onPressEnter={handleSearch}
                        style={{ width: 260 }}
                    />
                    <Select
                        loading={storesLoading}
                        value={filters.shopifyStoreId}
                        onChange={(value) => handleFilterChange({ shopifyStoreId: value })}
                        style={{ width: 280 }}
                    >
                        <Option value="all">All stores</Option>
                        {storeOptions.map(option => (
                            <Option key={option.value} value={option.value}>{option.label}</Option>
                        ))}
                    </Select>
                    <Select
                        value={filters.status}
                        onChange={(value) => handleFilterChange({ status: value })}
                        style={{ width: 130 }}
                    >
                        <Option value="active">Active</Option>
                        <Option value="inactive">Inactive</Option>
                        <Option value="all">All status</Option>
                    </Select>
                    <Select
                        value={filters.collectionType}
                        onChange={(value) => handleFilterChange({ collectionType: value })}
                        style={{ width: 130 }}
                    >
                        <Option value="all">All types</Option>
                        <Option value="custom">Custom</Option>
                        <Option value="smart">Smart</Option>
                    </Select>
                    <Tooltip title="Search">
                        <Button icon={<SearchOutlined />} onClick={handleSearch} />
                    </Tooltip>
                    <Tooltip title="Clear filters">
                        <Button icon={<ClearOutlined />} onClick={handleClearFilters} />
                    </Tooltip>
                    <Tooltip title="Refresh list">
                        <Button icon={<ReloadOutlined />} onClick={() => fetchCollections()} />
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<SyncOutlined spin={syncing} />}
                        loading={syncing}
                        onClick={handleSyncCollections}
                    >
                        Sync Collections
                    </Button>
                </Space>

                <Table
                    rowKey="id"
                    dataSource={collections}
                    columns={columns}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: total => `${total} collections`
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1380 }}
                    size="middle"
                />
            </Space>
        </div>
    );
};

export default ShopifyCollectionSettings;
